import api from "@/lib/api";
import { trackMeta } from "@/lib/meta";

const RZP_SRC = "https://checkout.razorpay.com/v1/checkout.js";

export function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = RZP_SRC;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

// Runs the full Razorpay checkout for a paid plan (requires an authenticated session).
// Calls onSuccess({ plan, paymentId }) after verify-payment, onFailure(msg) on error,
// and onDismiss() if the user closes the modal without paying.
export async function startPlanCheckout({ plan, billingPeriod = "monthly", onSuccess, onFailure, onDismiss }) {
  const ok = await loadRazorpay();
  if (!ok || !window.Razorpay) {
    onFailure?.("Failed to load payment gateway");
    return;
  }
  let data;
  try {
    ({ data } = await api.post("/billing/create-order", { plan, billing_period: billingPeriod }));
  } catch (e) {
    onFailure?.(e?.response?.data?.detail || "This plan can't be purchased right now");
    return;
  }
  const periodLabel = billingPeriod === "annual" ? "annual" : "monthly";
  const rzp = new window.Razorpay({
    key: data.razorpay_key_id,
    amount: data.amount,
    currency: data.currency,
    order_id: data.order_id,
    name: "RecoverOS",
    description: `${plan} plan — ${periodLabel} subscription`,
    prefill: data.prefill || {},
    theme: { color: "#0B3D91" },
    modal: { ondismiss: () => onDismiss?.() },
    handler: async (resp) => {
      try {
        await api.post("/billing/verify-payment", {
          razorpay_order_id: resp.razorpay_order_id,
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_signature: resp.razorpay_signature,
          plan,
        });
        const value = data.amount ? data.amount / 100 : undefined;
        const email = data.prefill?.email || null;
        const contact = data.prefill?.contact || null;
        trackMeta("Purchase", {
          customData: { value, currency: data.currency || "INR", plan_type: plan, content_name: `${plan} plan` },
          email, phone: contact,
        });
        trackMeta("Subscribe", {
          customData: { value, currency: data.currency || "INR", plan_type: plan },
          email, phone: contact,
        });
        window.dispatchEvent(new Event("recoveros:usage-changed"));
        onSuccess?.({ plan, paymentId: resp.razorpay_payment_id });
      } catch (e) {
        onFailure?.(e?.response?.data?.detail || "Payment could not be verified");
      }
    },
  });
  rzp.on("payment.failed", (r) => onFailure?.(r?.error?.description || "Payment failed"));
  rzp.open();
}
